import axios from 'axios'
import qs from 'qs'
import { Message } from 'element-ui'
import store from '@/store'
import router from '@/router'
import { convertPageNumAndSizeInRequest, convertTableBodyInResponse } from './convert'
import { forOwn } from 'lodash-es'
import { getHrefParams } from '@/utils/utils'
import TMessageBox from '@/components/tMessageBox'

const toast = function (message) {
  import('@/lang').then(({ default: i18n }) => {
    Message({
      type: 'error',
      message: i18n.t(message)
    })
  })
}

const service = axios.create({
  // 项目采用的是history路由 BASE_URL会与publicPath保持一致
  baseURL: process.env.BASE_URL,
  timeout: 120000 // 请求超时
})

// 判定url是否以list中的某一项结尾
const urlIsEndPoint = function (url, list) {
  return list.some(item => {
    const urlLength = url.length
    const length = item.length
    const index = url.indexOf(item)
    return index >= 0 && index + length === urlLength
  })
}

// request拦截器
service.interceptors.request.use(config => {
  const { data, convertPageNumAndSize, paramsType } = config
  // 转换pageNo和pageSize
  config.data = convertPageNumAndSizeInRequest(data, convertPageNumAndSize, paramsType)
  // 请求头中添加EMP_SID
  const { EMP_SID } = store.state.login.loginInfo
  if (EMP_SID) {
    config.headers.EMP_SID = EMP_SID
    config.headers['X-SESSION-ID'] = EMP_SID
  }
  return config
}, error => {
  console.warn(error) // for debug
  Promise.reject(error)
})

// response 拦截器
service.interceptors.response.use(response => {
  const { redirect_uri: redirectUri } = getHrefParams()
  const { data, config: { url, convertPageNumAndSize } } = response
  // 查询某一账户余额接口 accountBalanceQuery.do CI0004 'RECORD NOT FOUND'
  const responseCodeList = ['CI0004']
  /* corporbank旧接口 */
  // 1.某些不规范接口没有 ec em 字段
  const badApiList = ['SrandNum']
  // 2.某些接口业务逻辑特殊 具体的返回码需要交给实际调用时处理
  const returnCodeApiList = ['/api/faceId/faceResult']
  /* 转换表格接口返回格式 */
  response.data = convertTableBodyInResponse(data, convertPageNumAndSize)
  if (urlIsEndPoint(url, badApiList) || urlIsEndPoint(url, returnCodeApiList)) {
    return data
  } else if (data.constructor === Blob) {
    return data
  } else {
    if ((data && data.ec === '0') || (data.ec && responseCodeList.includes(data.ec)) || (data.retCode === '0000')) {
      return data.data || data.body || data || true
    } else if (data && (data.ec === '20000' || data.em === '管理台指令解包失败')) {
      // || data.retCode === '0001'
      store.commit('login/RESET_LOGIN_STATE')
      // 销毁所有的message， 防止失败状态多次弹出
      Message.closeAll()
      toast(data.em || data.retMsg)
      // TODO: 多个接口并发时导致报错
      if (redirectUri) {
        router.replace({
          name: 'Login',
          query: {
            redirect_uri: window.decodeURIComponent(redirectUri)
          }
        })
      } else {
        router.replace({
          name: 'Login'
        })
      }
      return Promise.reject(data)
    } else {
      const talertCodeMap = new Map([
        ['RESET_PWD_NOT_MANAGER', '您的密码重置申请已提交，请联系您的管理员操作重置密码']
      ])
      // 庐山接口需要特殊提醒的code值
      const targetLsCodeMap = {
        CERTIFICATE_EXPIRE: '您的证件已过期，为避免影响您的正常使用，请及时上传新的有效证件'
      }
      // 部分ec做alert提示处理
      if (talertCodeMap.has(data.ec)) {
        TMessageBox.alert(data.em || data.retMsg, '系统提示')
      } else if (targetLsCodeMap[data.retCode]) {
        TMessageBox.alert(targetLsCodeMap[data.retCode], '系统提示')
      } else {
        toast(data.em || data.retMsg)
      }
      // return false 改为 return Promise.reject(data)。这样的话，会直接进catch块，减少then块的逻辑处理。
      // 在每个catch块中单独打印 因为then块的代码也有可能执行出错
      // console.warn(error)
      return Promise.reject(data)
    }
  }
},
error => {
  if (error.message.includes('timeout')) { // 判断请求异常信息中是否含有超时timeout字符串
    console.error('Network timeout')
    toast('Network timeout')
    return Promise.reject(error)
  }
  console.warn('err' + error) // for debug
  toast(error)
  return Promise.reject(error)
})

// 由于axios的get和post方法传递参数有差异 进行二次封装下
const get = (url, params, config) => {
  const { EMP_SID } = store.state.login.loginInfo
  if (EMP_SID) {
    params.EMP_SID = EMP_SID
  }
  params.channel = '1103'
  params.responseFormat = 'JSON'
  return service({
    url,
    method: 'get',
    params,
    // 由于部分后端接口的get形式需要声明'Content-Type': 'application/json'，这里默认传一个data，axios会默认设置JSON。而如果不传data的话，axios会默认把自定义的Content-Type删除
    data: {},
    ...config
  })
}
const post = (url, params = {}, config, dataType = 'JSON') => {
  // 对于宇信的接口（以.do结尾） 添加固定请求字段
  // if (/\.do$/.test(url)) {
  // }
  const { EMP_SID } = store.state.login.loginInfo
  if (EMP_SID) {
    params.EMP_SID = EMP_SID
  }
  params.channel = '1103'
  params.responseFormat = 'JSON'
  let data = {
    ...params
  }
  // 由于企业网银旧版本接口采用的是FormData或者URLEncoded形式的，新接口是JSON形式的。这里做一层兼容判断
  if (dataType === 'URLEncoded') {
    // 利用qs对参数进行编码，并设置Content-Type为application/x-www-form-urlencoded
    if (Object.values(params).some(Array.isArray)) {
      // 存在值为数组的参数
      let queryString = ''
      forOwn(params, (value, key) => {
        if (Array.isArray(value)) {
          value.forEach(subValue => {
            queryString += (`${encodeURIComponent(key)}=${encodeURIComponent(subValue)}&`)
          })
        } else {
          queryString += (`${encodeURIComponent(key)}=${encodeURIComponent(value)}&`)
        }
      })
      // 去除末尾&
      data = queryString.slice(0, -1)
    } else {
      data = qs.stringify(params)
    }

    const headers = config.headers || {}
    config = {
      ...config,
      headers: {
        ...headers,
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      }
    }
  }
  if (dataType === 'FormData') {
    data = new FormData()
    for (const k in params) {
      data.append(k, params[k])
    }
  }
  return service({
    url,
    method: 'post',
    data,
    ...config
  })
}
// 下载文件
const file = (url, formData, config) => {
  return service({
    url,
    method: 'post',
    // 如果是formData形式的post请求 注意这里的参数形式。因为new FormData()后即使执行append，实例formData依然是一个空对象。如果利用上面es6的语法，参数data自然也是空对象。理想情况应该是file：(binary)。
    data: formData,
    ...config
  })
}

export default {
  get,
  post,
  file
}
